import React from 'react';
import { CheckCircle, LayoutDashboard, ArrowRight, ShieldCheck, Zap, Bell, FolderOpen, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from '../SEO';

const ApplicationTracker: React.FC = () => {
    const navigate = useNavigate();

    const handleActionClick = () => {
        navigate('/eligibility?service=admissions');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20">
            <SEO
                title="Automated Application Tracker | NexOverseas"
                description="Centralised, intelligent application management with real-time visibility. Track every milestone of your university application journey."
                canonical="/admissions/application-tracker"
            />

            <div className="bg-[#009B8F] text-white py-16 mb-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <span className="text-white/80 font-bold tracking-widest text-xs uppercase mb-4 block">Admissions Suite</span>
                    <h1 className="text-4xl md:text-5xl font-serif mb-4">Automated Application Tracker</h1>
                    <p className="text-xl text-white/90 max-w-2xl leading-relaxed">
                        Centralised, intelligent application management with real-time visibility.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                    <div className="prose prose-lg text-slate-600 max-w-none">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 bg-[#009B8F]/10 rounded-full">
                                <LayoutDashboard className="text-[#009B8F] w-8 h-8" />
                            </div>
                            <h2 className="text-3xl font-serif text-[#011E26] m-0">Complete Visibility & Control</h2>
                        </div>

                        <p className="leading-relaxed mb-6">
                            Managing multiple university applications across different countries often leads to scattered
                            emails, missed deadlines, and unnecessary stress. NexOverseas replaces this outdated
                            process with an Automated Application Tracker, a unified dashboard that gives students
                            complete visibility and control over every stage of their application journey.
                        </p>

                        <p className="leading-relaxed mb-6">
                            Students upload their verified documents once, and the platform securely stores them in a
                            structured, GDPR-compliant vault. Our smart internal auto-fill tools streamline workflows by
                            allowing our team to map your information efficiently across multiple university systems,
                            reducing repetitive data entry and minimising errors.
                        </p>

                        <p className="leading-relaxed mb-8">
                            Instead of relying on manual email chains, students receive instant updates directly through
                            the dashboard as our staff progress each stage of the application. The system acts as your
                            digital project manager, ensuring every requirement, milestone, and deadline is clearly
                            tracked from drafting to enrolment.
                        </p>

                        <div className="bg-[#011E26] text-white p-8 rounded-sm mb-12">
                            <h3 className="text-xl font-serif mb-8 text-[#009B8F]">How the Automated Application Tracker Works</h3>
                            <div className="space-y-8">
                                {[
                                    { icon: <LayoutDashboard size={24} />, title: "Centralised Application Dashboard", desc: "A single, Kanban-style interface displays all your applications, tasks, deadlines, and progress updates in one organised view." },
                                    { icon: <Zap size={24} />, title: "Smart Internal Auto-Fill", desc: "Your verified profile data is securely stored and used by our team to streamline application preparation across multiple portals." },
                                    { icon: <BarChart3 size={24} />, title: "Real-Time Status Updates", desc: "As our staff complete each stage — submission, document checks, interview scheduling, offer follow-ups, your dashboard updates instantly." },
                                    { icon: <Bell size={24} />, title: "Automated Alerts & Milestone Reminders", desc: "You receive timely notifications for upcoming deadlines, missing documents, interview dates, and payment milestones." },
                                    { icon: <ShieldCheck size={24} />, title: "Secure Document Vault", desc: "A GDPR-compliant cloud vault stores all your academic records, financial documents, and identity files for instant retrieval." }
                                ].map((step, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="text-[#009B8F] mt-1">{step.icon}</div>
                                        <div>
                                            <h4 className="font-bold text-sm tracking-wider uppercase mb-1">{step.title}</h4>
                                            <p className="text-xs text-slate-400 leading-relaxed">{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={handleActionClick}
                            className="group inline-flex items-center gap-3 bg-[#009B8F] text-white px-8 py-4 font-bold uppercase tracking-widest text-xs hover:bg-[#011E26] transition-all duration-300 rounded-sm shadow-xl"
                        >
                            Run a check with AI <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="space-y-8 sticky top-32">
                        <div className="relative rounded-sm overflow-hidden shadow-2xl border border-slate-200 p-2 bg-white">
                            <img
                                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=800"
                                alt="Application tracking dashboard"
                                className="w-full h-[500px] object-cover rounded-sm"
                            />
                            <div className="absolute top-8 right-8 bg-[#009B8F] text-white px-4 py-2 rounded shadow-lg text-[10px] font-bold tracking-widest uppercase">
                                Real-time Sync
                            </div>
                        </div>

                        <div className="bg-slate-50 p-6 rounded-sm border border-slate-200">
                            <div className="flex items-center gap-3 mb-4 text-[#009B8F]">
                                <ShieldCheck size={20} />
                                <span className="text-xs font-bold uppercase tracking-widest">GDPR Compliant & Secure</span>
                            </div>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Your data is protected with enterprise-grade encryption and stored in sovereign cloud infrastructure, ensuring your personal documents remain private and secure.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicationTracker;
